import React from "react";
import { useTranslation } from "react-i18next";
import { LuX } from "react-icons/lu";
import { CRITERIA_FIELDS, emptyCriteria, parseNumber } from "../../utils/propertyRequestForm";
import { amount, area, categoryLabel, cityLabel, delegationLabel } from "./labels";

// Criteria found by the analysis or entered by the user, each with its origin; removable one by one.
export default function DetectedCriteria({ criteria, sources, unscored = [], onRemove }) {
  const { t } = useTranslation();
  const number = (value) => parseNumber(value);
  const label = {
    transaction: () => t(`propertyRequest.transaction.${criteria.transaction}`),
    categories: () => criteria.categories.map((c) => categoryLabel(t, c, criteria.transaction)).join(" · "),
    city: () => cityLabel(t, criteria.city),
    delegations: () => criteria.delegations.map((d) => delegationLabel(t, criteria.city, d)).join(" · "),
    budgetMin: () => amount(t, criteria.transaction)(number(criteria.budgetMin)),
    budgetMax: () => amount(t, criteria.transaction)(number(criteria.budgetMax)),
    surfaceMin: () => area(number(criteria.surfaceMin)),
    surfaceMax: () => area(number(criteria.surfaceMax)),
    bedroomsMin: () => t("propertyRequest.summary.bedroomsValue", { count: number(criteria.bedroomsMin) }),
    attributes: () => criteria.attributes.map((a) => t(`propertyRequest.attribute.${a}`)).join(" · "),
  };
  const name = {
    transaction: "criteria.transaction", categories: "criteria.categories", city: "criteria.city", delegations: "criteria.delegations",
    budgetMin: "summary.budget", budgetMax: "summary.budget", surfaceMin: "summary.surface", surfaceMax: "summary.surface",
    bedroomsMin: "summary.bedrooms", attributes: "summary.attributes",
  };
  const prefix = { budgetMin: "min", surfaceMin: "min", budgetMax: "max", surfaceMax: "max" };
  const rows = CRITERIA_FIELDS.filter((field) => sources[field] && JSON.stringify(criteria[field]) !== JSON.stringify(emptyCriteria()[field]));
  if (rows.length === 0 && unscored.length === 0) return null;
  return (
    <section className="rounded-3xl border border-green-200 bg-white shadow-sm" aria-labelledby="pr-detected-title">
      <h2 id="pr-detected-title" className="border-b border-slate-100 px-4 py-3 text-lg font-bold text-slate-900 sm:px-6">{t("propertyRequest.analysis.detected")}</h2>
      {rows.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {rows.map((field) => {
            const value = label[field]();
            const title = `${t(`propertyRequest.${name[field]}`)}${prefix[field] ? ` ${t(`propertyRequest.criteria.${prefix[field]}`)}` : ""}`;
            return (
              <li key={field} className="flex items-start justify-between gap-3 px-4 py-3 sm:px-6" data-detected={field}>
                <div className="min-w-0">
                  <p className="text-sm text-slate-500">
                    {title} · <span className={sources[field] === "ai" ? "font-semibold text-green-800" : ""}>{t(`propertyRequest.analysis.${sources[field]}`)}</span>
                  </p>
                  <p className="break-words text-base font-semibold text-slate-900">{value}</p>
                </div>
                <button type="button" onClick={() => onRemove(field)} aria-label={t("propertyRequest.analysis.remove", { label: `${title} ${value}` })}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-green-500/30">
                  <LuX aria-hidden="true" size={18} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {unscored.length > 0 && (
        <div className="border-t border-slate-100 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:px-6" data-unscored>
          <p className="font-semibold">{unscored.map((u) => t(`propertyRequest.attribute.${u}`)).join(" · ")}</p>
          <p>{t("propertyRequest.analysis.unscored")}</p>
        </div>
      )}
    </section>
  );
}
